import { useState, useRef, useEffect } from "react";
import { api, API } from "@/lib/api";
import { getVoice } from "@/lib/voices";
import { startNarration, endNarration, stopNarration } from "@/lib/narrator";
import { Headphones, Play, Pause, Loader2, RotateCw } from "lucide-react";
import { SpeakingIndicator } from "@/components/SpeakingIndicator";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

/**
 * One-tap 60-second spoken board briefing — auto-written from the live risk
 * picture and narrated via ElevenLabs (pre-cached overnight, so it plays
 * effectively instantly). Something execs can listen to on the way in.
 */
export const BoardBriefing = () => {
  const { user } = useAuth();
  const canRefresh = user && (user.role === "admin" || user.role === "cro");
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [text, setText] = useState(null);
  const [fresh, setFresh] = useState(null);
  const [error, setError] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const audioRef = useRef(null);

  const reloadStatus = () =>
    api.get("/voice/board-briefing/status").then(({ data }) => setFresh(data)).catch(() => setFresh(null));

  const refresh = async () => {
    if (refreshing) return;
    setRefreshing(true);
    try {
      const { data } = await api.post("/voice/board-briefing/refresh");
      if (data?.status === "generated") {
        toast.success("Today's briefing generated.");
      } else if (data?.status === "already_generated") {
        toast.info("Today's briefing is already up to date.");
      }
      await reloadStatus();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't refresh today's briefing.");
    } finally {
      setRefreshing(false);
    }
  };

  useEffect(() => {
    reloadStatus();
  }, []);

  // iter135 — kill audio on unmount AND on logout. Without this, the
  // <audio> element keeps streaming after the user signs out (the SPA
  // re-renders to /login but the HTMLAudioElement stays in memory and
  // continues consuming ElevenLabs bytes + speaking out loud).
  useEffect(() => {
    const tearDown = () => {
      const a = audioRef.current;
      if (a) {
        try { a.pause(); } catch { /* noop */ }
        try { a.src = ""; } catch { /* noop */ }
        audioRef.current = null;
      }
      // Also clear the global narrator singleton in case something else
      // (Ask ERIO FAB, NLQ bar) is playing.
      try { stopNarration(); } catch { /* noop */ }
    };
    window.addEventListener("erio:logout", tearDown);
    return () => {
      window.removeEventListener("erio:logout", tearDown);
      tearDown();
    };
  }, []);

  const freshLabel = () => {
    if (!fresh?.available || !fresh.generated_at) return null;
    const d = new Date(fresh.generated_at);
    const today = new Date().toDateString() === d.toDateString();
    const t = d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
    return today ? `Updated ${t} today` : `Updated ${d.toLocaleDateString()}`;
  };

  const toggle = async () => {
    if (audioRef.current) {
      if (audioRef.current.paused) {
        startNarration(audioRef.current, () => setPlaying(false));
        audioRef.current.play().catch(() => {});
      } else {
        audioRef.current.pause();
      }
      return;
    }
    setLoading(true);
    setError(false);
    try {
      const { data } = await api.get(`/voice/board-briefing?voice=${encodeURIComponent(getVoice())}`);
      setText(data.text);
      const audio = new Audio(`${API}/voice/stream/${data.nid}`);
      audioRef.current = audio;
      audio.onplay = () => setPlaying(true);
      audio.onpause = () => setPlaying(false);
      audio.onended = () => { endNarration(audio); setPlaying(false); };
      audio.onerror = () => { setError(true); setPlaying(false); };
      startNarration(audio, () => setPlaying(false));
      await audio.play().catch(() => {});
    } catch (err) {
      setError(true);
      const detail = err.response?.data?.detail;
      setErrorMsg(detail || "");
      toast.error(detail || "Board briefing unavailable.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="mt-8 flex flex-col gap-4 rounded-md border border-ek-purple/15 bg-gradient-to-br from-ek-purple/[0.05] to-transparent p-5 sm:flex-row sm:items-center"
      data-testid="board-briefing-card"
    >
      <button
        onClick={toggle}
        disabled={loading}
        data-testid="board-briefing-play"
        aria-label={playing ? "Pause board briefing" : "Play board briefing"}
        className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-ek-purple text-white shadow-md transition-transform hover:scale-105 disabled:opacity-60"
      >
        {loading ? <Loader2 size={22} className="animate-spin" /> : playing ? <Pause size={22} /> : <Play size={22} className="ml-0.5" />}
      </button>
      <div className="min-w-0 flex-1">
        <h3 className="flex flex-wrap items-center gap-2 font-display text-base font-semibold text-ek-ink">
          <Headphones size={16} className="text-ek-purple" /> Your 60-second board briefing
          {playing && <SpeakingIndicator className="ml-1 h-3 text-ek-purple" />}
          {!playing && freshLabel() && (
            <span className="inline-flex items-center gap-1 text-[11px] font-medium text-emerald-600" data-testid="board-briefing-freshness">
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" /> {freshLabel()}
            </span>
          )}
        </h3>
        {text ? (
          <p className="mt-1.5 text-sm leading-relaxed text-ek-ink/70" data-testid="board-briefing-text">{text}</p>
        ) : error ? (
          <p className="mt-1 flex flex-wrap items-center gap-2 text-sm text-red-600" data-testid="board-briefing-error">
            {errorMsg || "Couldn't prepare your briefing."}
            <button onClick={() => { setError(false); setErrorMsg(""); toggle(); }} data-testid="board-briefing-retry"
              className="inline-flex items-center gap-1 rounded-md border border-red-200 bg-white px-2 py-0.5 text-[12px] font-semibold text-red-600 hover:bg-red-50">
              <RotateCw size={11} /> Retry
            </button>
          </p>
        ) : (
          <p className="mt-1 text-sm text-ek-ink/65">
            {loading ? "Preparing your briefing…" : (fresh && !fresh.available)
              ? (canRefresh
                  ? "Today's briefing hasn't been generated yet — click Refresh to create it."
                  : "Today's briefing hasn't been generated yet. Ask an admin or CRO to refresh it.")
              : "AI-written from today's live risk picture — listen on your way into the meeting."}
          </p>
        )}
      </div>
      {canRefresh && fresh && !fresh.available && (
        <button
          onClick={refresh}
          disabled={refreshing}
          data-testid="board-briefing-refresh"
          className="inline-flex shrink-0 items-center gap-1.5 self-start rounded-md border border-ek-purple/30 bg-white px-3 py-1.5 text-xs font-semibold text-ek-purple shadow-sm transition hover:bg-ek-purple/5 disabled:opacity-60 sm:self-center"
          title="Generate today's briefing (one ElevenLabs synth pass)"
        >
          {refreshing ? <Loader2 size={13} className="animate-spin" /> : <RotateCw size={13} />}
          {refreshing ? "Refreshing…" : "Refresh today's briefing"}
        </button>
      )}
    </div>
  );
};
